import React from 'react';
import {
  CheckCircle2,
  Cloud,
  GraduationCap,
  Headphones,
  Layers,
  Cpu,
  Settings,
} from 'lucide-react';

const services = [
  {
    id: 1,
    title: 'Implementation & Rollout',
    subtitle: 'Turnkey Multi-Facility Deployment',
    description:
      'Structured Infor CSI implementation designed around your specific operating cadence. We blueprint business processes, configure complex BOMs and shop routing, validate scenarios, and steer your organization to a confident go-live.',
    icon: Layers,
    accentColor: '#1D4ED8',
    bgColor: '#EFF6FF',
    badge: 'Turnkey Delivery',
    deliverables: [
      'Fit-Gap & Process Harmonization',
      'Multi-Level BOM & Routing Configuration',
      'UAT & Operational Simulation',
      'Cutover Execution & Hypercare',
    ],
  },
  {
    id: 2,
    title: 'Customization & Mongoose Dev',
    subtitle: 'Upgrade-Safe Application Engineering',
    description:
      'Tailor Infor CSI to unique business models without breaking upgradeability. We utilize the Infor Mongoose framework to build custom workspaces, automated triggers, validated forms, and automated alerts.',
    icon: Settings,
    accentColor: '#0284C7',
    bgColor: '#F0F9FF',
    badge: '100% Upgrade Safe',
    deliverables: [
      'Infor Mongoose Framework Applications',
      'Custom Role-Based Workspaces',
      'Event-Driven Workflow Automation',
      'Executive Dashboards & BI Feeds',
    ],
  },
  {
    id: 3,
    title: 'Cloud Migration & Transition',
    subtitle: 'From On-Premise to AWS CloudSuite',
    description:
      'Seamlessly modernize from on-premise SyteLine or legacy ERPs to Infor multi-tenant cloud on AWS. We sanitize legacy databases, re-architect integrations, and execute zero-downtime cutovers.',
    icon: Cloud,
    accentColor: '#059669',
    bgColor: '#ECFDF5',
    badge: 'Zero Downtime',
    deliverables: [
      'Legacy Schema & Customization Audit',
      'Automated Data Extraction & Cleansing',
      'Multi-Tenant AWS Architecture Setup',
      'Parallel Production Verification',
    ],
  },
  {
    id: 4,
    title: 'Training & Change Enablement',
    subtitle: 'Rapid User Mastery & Adoption',
    description:
      'Accelerate user adoption through structured, role-specific curriculum. We coach shop floor managers, planners, procurement officers, and accountants with real production scenarios and digitized SOPs.',
    icon: GraduationCap,
    accentColor: '#4F46E5',
    bgColor: '#EEF2FF',
    badge: 'Role-Based Curriculum',
    deliverables: [
      'Interactive Role-Based Simulations',
      'Standard Operating Procedure (SOP) Kits',
      'Executive KPI Cockpit Walkthroughs',
      'Post-Launch Reinforcement Coaching',
    ],
  },
  {
    id: 5,
    title: '24/7 Managed SLA Support',
    subtitle: 'Proactive Health & Continuous Evolution',
    description:
      'Ensure uninterrupted industrial continuity with our global follow-the-sun support. We deliver SLA-backed incident management, periodic health audits, Infor release regression testing, and continuous tuning.',
    icon: Headphones,
    accentColor: '#0891B2',
    bgColor: '#ECFEFF',
    badge: '24/7 Follow-the-Sun',
    deliverables: [
      'Guaranteed Response SLAs & Tier-3 Escalation',
      'Quarterly Cloud Release Testing',
      'Database Indexing & Performance Tuning',
      'Continuous Backlog Enhancement',
    ],
  },
];

const CSIServices = () => {
  return (
    <section
      className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className="mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-6xl mx-auto mb-16">

          <h2
            className="font-satoshi text-2xl font-bold leading-tight sm:text-3xl md:text-4xl"
            style={{ color: '#0F172A', fontFamily: "'Satoshi', sans-serif" }}
          >
            End-to-End Infor CSI Consulting Services
          </h2>

          <p
            className="mt-4 text-base sm:text-lg leading-relaxed"
            style={{ color: '#475569' }}
          >
            Comprehensive advisory, modernization, engineering, and managed services built to maximize 
            your Infor CloudSuite Industrial investment across every phase of the lifecycle.
          </p>
        </div>

        {/* Services Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const ServiceIcon = service.icon;
            return (
            <div
              key={service.id}
              className={`group flex flex-col justify-between rounded-2xl border p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl ${
                index === 4 ? 'md:col-span-2 lg:col-span-1' : ''
              }`}
              style={{
                backgroundColor: '#FFFFFF',
                borderColor: '#E2E8F0',
                boxShadow: '0 8px 30px rgba(15, 23, 42, 0.04)',
              }}
            >
              <div>
                {/* Header Row: Icon + Badge */}
                <div className="flex items-center justify-between mb-6">
                  <div
                    className="flex h-16 w-16 items-center justify-center rounded-2xl p-2.5 transition-transform duration-300 group-hover:scale-105 border"
                    style={{
                      backgroundColor: service.bgColor,
                      borderColor: '#E2E8F0',
                    }}
                  >
                    <ServiceIcon
                      aria-hidden="true"
                      className="h-10 w-10"
                      style={{ color: service.accentColor }}
                      strokeWidth={1.8}
                    />
                  </div>

                  <span
                    className="inline-flex rounded-full border px-3 py-1 text-xs font-bold"
                    style={{
                      backgroundColor: service.bgColor,
                      borderColor: '#CBD5E1',
                      color: service.accentColor,
                    }}
                  >
                    {service.badge}
                  </span>
                </div>

                {/* Service Title & Subtitle */}
                <span
                  className="text-xs font-bold uppercase tracking-wider block mb-1"
                  style={{ color: service.accentColor }}
                >
                  {service.subtitle}
                </span>

                <h3
                  className="text-xl font-bold tracking-tight mb-3"
                  style={{ color: '#0F172A' }}
                >
                  {service.title}
                </h3>

                {/* Description */}
                <p
                  className="text-sm leading-relaxed mb-6 font-normal"
                  style={{ color: '#475569' }}
                >
                  {service.description}
                </p>
              </div>

              {/* Deliverables Checklist */}
              {/* <div
                className="pt-5 border-t space-y-2.5"
                style={{ borderColor: '#F1F5F9' }}
              >
                <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748B' }}>
                  Key Capabilities:
                </div>
                {service.deliverables.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2.5">
                    <CheckCircle2
                      className="h-4 w-4 shrink-0 mt-0.5"
                      style={{ color: service.accentColor }}
                    />
                    <span className="text-xs font-medium" style={{ color: '#1E293B' }}>
                      {item}
                    </span>
                  </div>
                ))}
              </div> */}
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CSIServices;
